import React, { useState, useEffect, useCallback } from "react";

//routing
import { useParams } from "react-router-dom";

//components
import DataTable from "../../../../components/Tables/DataTable";

//hooks
import useLazyQueryAuth from "../../../../hooks/useLazyQueryAuth";
import useQueryAuth from "../../../../hooks/useQueryAuth";
import useAuth from "../../../../hooks/useAuth";
import useMutationAuth from "../../../../hooks/useMutationAuth";

//graphql
import { GET_ALL_ROLES, DELETE_ROLES } from "../../../../graphql/role";
import { GET_USER_TABLE_PREFERENCES } from "../../../../graphql/userTablePreferences";

//editors
import { Parse } from "../../../../JSON.editor";

//mnemonic values
import Mnemonic from "../../../../Mnemonics.json";

//urls
import urls from "../../../../urls";

//external components
import { CircularProgress } from "@mui/material";

import ContainerCard from "../../../../components/Cards/ContainerCard";
import UTC2Local from "../../../../UTC2Local";
import componentDisplay from "../../../../componentDisplay";
import { useSnackbar } from "notistack";

import { useTranslation } from "react-i18next";
import { removeNullProps } from "../../../../utils/removeNullProps";
import { decodeUnicodeString } from "../../../../utils/decodeUnicodeString";
const RolesList = ({ fromTenant = false, myRoles = false }) => {
  const { tID, tName } = useParams();

  const {
    new_tenant_role,
    tenant_role_form,
    my_roles_form,
    my_new_role,
  } = urls;

  const { storeUserGuid, storeUserTenant, userRoles } = useAuth();

  const { manageParameters } = userRoles;

  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const [re, setRe] = useState(false);
  const [data, setData] = useState(null);
  const [defaultFilters, setDefaultFilters] = useState({});
  const [columns, setColumns] = useState([]);
  const [order, setOrder] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState("");

  const { mfunction: deleteRecords, loading: deleteLoading } = useMutationAuth(
    DELETE_ROLES,
    "DeleteRolesList"
  );

  const filtersObject = [
    {
      type: "text",
      label: "roles.list.filters.name",
      name: "name",
    },
    {
      type: "text",
      label: "roles.list.filters.tenant",
      name: "tenant",
      skip: fromTenant || myRoles,
    },
  ];

  const {
    loading: getColumnsLoading,
    tokenChange: columnsWait,
    refetch,
  } = useQueryAuth(
    GET_USER_TABLE_PREFERENCES,
    "GetUserTablePreferences",
    {
      variables: {
        model: {
          table: Mnemonic.Tables["Roles"],
          userID: storeUserGuid,
        },
      },
    },
    {
      onCompleted: (response) => {

        let col =
          response &&
          response.GetUserTablePreferences &&
          response.GetUserTablePreferences.columns
            ? response.GetUserTablePreferences.columns
            : null;

        if (col) {
          col = decodeUnicodeString(col);
          //we parse the json string to a json object
          const parsed = JSON.parse(col, (key, value) =>
            Parse(key, value, UTC2Local, componentDisplay)
          );

          if (parsed.columns) {
            let cols = parsed.columns;

            cols.map(c => {
              c = removeNullProps(c);
              return c;
            })
            setColumns(cols);
          }

          if (parsed.defaultSort) setOrderBy(parsed.defaultSort);

          if (parsed.defaultSortDirection)
            setOrder(parsed.defaultSortDirection);
        }
      },
    }
  );

  const { mfunction: getAllROles, loading } = useLazyQueryAuth(
    GET_ALL_ROLES,
    "GetAllRoles"
  );

  const fetchTableData = useCallback(
    (
      page = 0,
      pageSize = 0,
      order = "",
      orderBy = "",
      search = "",
      filters = null
    ) => {
      getAllROles(
        {
          variables: {
            model: {
              enableFilters: true,
              keys: {
                page: page + 1,
                pageSize: pageSize,
              },
              orderColumn: orderBy,
              orderDirection: order,
              keyWord: search,
              tenantID: myRoles
                ? storeUserTenant
                : tID && fromTenant
                ? tID
                : undefined,
              userID: storeUserGuid,
              filters: filters,
            },
          },
        },
        (response) => {
          setData(response);
        }
      );
    },
    []
  );

  useEffect(() => {
    if (!columnsWait)
      fetchTableData(page, rowsPerPage, order, orderBy, search, defaultFilters);
  }, [columnsWait, fetchTableData, re]);

  const rows =
    data &&
    data.GetAllRoles &&
    data.GetAllRoles.rolesList &&
    data.GetAllRoles.rolesList.roles
      ? data.GetAllRoles.rolesList.roles
      : [];
  const numberOfRecords =
    data &&
    data.GetAllRoles &&
    data.GetAllRoles.rolesList &&
    data.GetAllRoles.rolesList.totalNumberOfRecords
      ? data.GetAllRoles.rolesList.totalNumberOfRecords
      : 0;

  const handleDelete = () => {
    deleteRecords(
      {
        variables: {
          model: {
            roleIDs: selected,
          },
        },
      },
      (response) => {
        enqueueSnackbar(t("role.list.delete.success"), {
          variant: "success",
        });
        setRe((v) => !v);
        setSelected([]);
        setPage(0);
      }
    );
  };

  const pagination = {
    rows,
    columns,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    selected,
    defaultFilters,
    setDefaultFilters,
    setSelected,
    search,
    setSearch,
    setPage,
    setRowsPerPage,
    numberRecords: numberOfRecords,
    page,
    rowsPerPage,
    fetchTableData,
    removeFilters:true,
    loading: loading || columnsWait,
    searchText: fromTenant
      ? "role.list.search.label.fromTenant"
      : "role.list.search.label",
    refetch,
    tableMnemonic: Mnemonic.Tables["Roles"],
    filtersObject,
    generateRoute: (row) =>
      myRoles
        ? my_roles_form.replace(
            "/:rName/:rID",
            `/${encodeURIComponent(row?.name)}/${encodeURIComponent(
              row?.roleGuid
            )}`
          )
        : tenant_role_form
            .replace(
              "/:tName/:tID",
              `/${encodeURIComponent(tName)}/${encodeURIComponent(tID)}`
            )
            .replace(
              "/:rName/:rID",
              `/${encodeURIComponent(row?.name)}/${encodeURIComponent(
                row?.roleGuid
              )}`
            ),
    newText: "role.list.new.label",
    newLink: fromTenant
      ? new_tenant_role.replace("/:tName/:tID", `/${encodeURIComponent(tName)}/${encodeURIComponent(tID)}`)
      : my_new_role,
    deleteText: "role.list.delete.label",
    handleDelete: handleDelete,
    deleteLoading,
    hideEditColumns: true,
    removeSelectAll: true,
    checkboxDisable: (row) => row.isSystemGenerated === true,
    readOnly: manageParameters !== "M",
  };

  return (
    <div>
      {getColumnsLoading ? (
        <ContainerCard
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <CircularProgress />
        </ContainerCard>
      ) : (
        <DataTable {...pagination} identifier={"roleGuid"} hasCheckbox />
      )}
    </div>
  );
};

export default RolesList;
